import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ExperienceDetails.css";
import API_BASE_URL from "../config";
import {
  FiBriefcase,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiCode,
  FiFileText,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiLoader,
  FiAward
} from "react-icons/fi";

const ExperienceDetails = ({ employee }) => {
  const [experience, setExperience] = useState({
    companyName: "",
    role: "",
    yearsOfExperience: "",
    description: "",
    startDate: "",
    endDate: "",
    location: "",
    skills: "",
  });

  const [experienceList, setExperienceList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Ensure employee emp_id exists
  const emp_id = employee?.emp_id;

  // Fetch experiences for this employee using emp_id
  const fetchExperiences = async () => {
    if (!emp_id) {
      setError("Employee emp_id not found!");
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError("");
      
      console.log(`🔍 Fetching experiences for emp_id: ${emp_id}`);
      
      const res = await axios.get(
        `${API_BASE_URL}/api/experience/by-emp-id/${emp_id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      console.log("Experience API response:", res.data);
      
      if (res.data.success) {
        setExperienceList(res.data.data);
      } else {
        setExperienceList([]);
        setError(res.data.message || "No experience records found");
      }
    } catch (err) {
      console.error("❌ Error fetching experiences:", err);
      setError(err.response?.data?.message || "Failed to fetch experience records");
      setExperienceList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, [emp_id]);

  const handleChange = (e) => {
    setExperience({ ...experience, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emp_id) {
      setError("Employee emp_id not found!");
      return;
    }

    try {
      setError("");
      
      const payload = {
        ...experience,
        employeeId: employee?._id,
        emp_id: emp_id,
        yearsOfExperience: parseInt(experience.yearsOfExperience) || 0
      };
      
      console.log("Submitting experience data:", payload);

      if (isEditing) {
        await axios.put(
          `${API_BASE_URL}/api/experience/${editId}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/experience`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      // Reset form
      setExperience({
        companyName: "",
        role: "",
        yearsOfExperience: "",
        description: "",
        startDate: "",
        endDate: "",
        location: "",
        skills: "",
      });
      setShowForm(false);
      setIsEditing(false);
      setEditId(null);

      // Refresh data
      fetchExperiences();
      
    } catch (err) {
      console.error("❌ Error saving experience:", err);
      setError(err.response?.data?.error || "Failed to save experience");
    }
  };

  const handleEdit = (exp) => {
    setExperience({ 
      ...exp,
      skills: Array.isArray(exp.skills) ? exp.skills.join(", ") : exp.skills || ""
    });
    setIsEditing(true);
    setEditId(exp._id);
    setShowForm(true);
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this experience?")) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/experience/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      // Refresh data
      fetchExperiences();
      
    } catch (err) {
      console.error("❌ Error deleting experience:", err);
      setError(err.response?.data?.error || "Failed to delete experience");
    }
  };

  const handleCancel = () => {
    setExperience({
      companyName: "",
      role: "",
      yearsOfExperience: "",
      description: "",
      startDate: "",
      endDate: "",
      location: "",
      skills: "",
    });
    setIsEditing(false);
    setEditId(null);
    setShowForm(false);
    setError("");
  };

  // Format skills for display
  const formatSkills = (skills) => {
    if (Array.isArray(skills)) {
      return skills.join(", ");
    }
    return skills || "";
  };

  return (
    <div className="exp-professional-container">
      <div className="exp-svg-background"></div>
      
      <h3 className="exp-gradient-title">
        <FiBriefcase style={{ marginRight: '10px', verticalAlign: 'middle' }} />
        Professional Experience ({experienceList.length})
      </h3>
      <p className="exp-employee-info">
        <FiUser style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Employee: {employee?.name || "N/A"} (ID: {emp_id || "N/A"})
      </p>
      
      {error && (
        <div className="exp-error-message">
          <span className="exp-error-icon"><FiAlertCircle /></span>
          {error}
        </div>
      )}
      
      <button 
        className="exp-add-button"
        onClick={() => setShowForm(true)}
      >
        <span className="exp-button-icon"><FiPlus /></span> Add New Experience
      </button>

      {showForm && (
        <div className="exp-form-overlay">
          <form onSubmit={handleSubmit} className="exp-professional-form">
            <div className="exp-form-header">
              <h4>
                <FiAward style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                {isEditing ? "Edit Experience" : "Add New Experience"}
              </h4>
              <button 
                type="button" 
                className="exp-close-button"
                onClick={handleCancel}
              >
                <FiX />
              </button>
            </div>
            
            <div className="exp-form-grid">
              <div className="exp-form-group">
                <label>Company Name *</label>
                <input
                  name="companyName"
                  value={experience.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  required
                />
              </div>
              
              <div className="exp-form-group">
                <label>Role/Position *</label>
                <input
                  name="role"
                  value={experience.role}
                  onChange={handleChange}
                  placeholder="Enter your role"
                  required
                />
              </div>
              
              <div className="exp-form-group">
                <label>Years of Experience *</label>
                <input
                  type="number"
                  name="yearsOfExperience"
                  value={experience.yearsOfExperience}
                  onChange={handleChange}
                  placeholder="Number of years"
                  required
                />
              </div>
              
              <div className="exp-form-group">
                <label>
                  <FiMapPin style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Location
                </label>
                <input
                  name="location"
                  value={experience.location}
                  onChange={handleChange}
                  placeholder="City, Country"
                />
              </div>
              
              <div className="exp-form-group exp-full-width">
                <label>
                  <FiCalendar style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={experience.startDate}
                  onChange={handleChange}
                />
              </div>
              
              <div className="exp-form-group exp-full-width">
                <label>
                  <FiCalendar style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={experience.endDate}
                  onChange={handleChange}
                />
              </div>
              
              <div className="exp-form-group exp-full-width">
                <label>
                  <FiCode style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Skills (comma separated)
                </label>
                <input
                  name="skills"
                  value={experience.skills}
                  onChange={handleChange}
                  placeholder="e.g. React, Node.js, MongoDB"
                />
              </div>
              
              <div className="exp-form-group exp-full-width">
                <label>
                  <FiFileText style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Description
                </label>
                <textarea
                  name="description"
                  value={experience.description}
                  onChange={handleChange}
                  placeholder="Describe your responsibilities and achievements"
                  rows="4"
                ></textarea>
              </div>
            </div>
            
            <div className="exp-form-actions">
              <button type="submit" className="exp-submit-button">
                <FiCheckCircle style={{ marginRight: '8px' }} />
                {isEditing ? "Update Experience" : "Save Experience"}
              </button>
              <button 
                type="button" 
                className="exp-cancel-button"
                onClick={handleCancel}
              >
                <FiX style={{ marginRight: '8px' }} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="exp-loading-container">
          <div className="exp-loading-spinner"></div>
          <p>
            <FiLoader style={{ 
              marginRight: '8px', 
              verticalAlign: 'middle',
              animation: 'spin 1s linear infinite'
            }} />
            Loading experience records...
          </p>
        </div>
      ) : experienceList.length === 0 ? (
        <div className="exp-empty-state">
          <div className="exp-empty-icon"><FiBriefcase /></div>
          <p>No experiences added yet.</p>
          <p>Click the button above to add your first experience.</p>
        </div>
      ) : (
        <div className="exp-cards-container">
          {experienceList.map((exp) => (
            <div key={exp._id} className="exp-professional-card">
              <div className="exp-card-header">
                <h4 className="exp-company-gradient">
                  <FiBriefcase style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  {exp.companyName}
                </h4>
                <div className="exp-card-actions">
                  <button 
                    className="exp-edit-btn"
                    onClick={() => handleEdit(exp)}
                    aria-label="Edit experience"
                    title="Edit experience"
                  >
                    <FiEdit2 />
                  </button>
                  <button 
                    className="exp-delete-btn"
                    onClick={() => handleDelete(exp._id)}
                    aria-label="Delete experience"
                    title="Delete experience"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              
              <div className="exp-card-content">
                <p className="exp-role">{exp.role}</p>
                
                <div className="exp-details-grid">
                  <div className="exp-detail-item">
                    <span className="exp-detail-label">
                      <FiClock style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Experience:
                    </span>
                    <span className="exp-detail-value">{exp.yearsOfExperience} years</span>
                  </div>
                  
                  {exp.location && (
                    <div className="exp-detail-item">
                      <span className="exp-detail-label">
                        <FiMapPin style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Location:
                      </span>
                      <span className="exp-detail-value">{exp.location}</span>
                    </div>
                  )}
                  
                  {exp.startDate && (
                    <div className="exp-detail-item">
                      <span className="exp-detail-label">
                        <FiCalendar style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Start Date:
                      </span>
                      <span className="exp-detail-value">
                        {new Date(exp.startDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {exp.endDate && (
                    <div className="exp-detail-item">
                      <span className="exp-detail-label">
                        <FiCalendar style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        End Date:
                      </span>
                      <span className="exp-detail-value">
                        {new Date(exp.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {exp.skills && exp.skills.length > 0 && (
                    <div className="exp-detail-item exp-full-width">
                      <span className="exp-detail-label">
                        <FiCode style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Skills:
                      </span>
                      <span className="exp-detail-value">
                        {formatSkills(exp.skills)}
                      </span>
                    </div>
                  )}
                </div>
                
                {exp.description && (
                  <div className="exp-description">
                    <p>
                      <FiFileText style={{ 
                        marginRight: '8px', 
                        verticalAlign: 'top',
                        marginTop: '2px'
                      }} />
                      {exp.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExperienceDetails;